// MixedFamilyIntegerToSinglePowerTask.js
// Podtemat 2 – potęga o wykładniku całkowitym
// Dokładnie 3 czynniki, zawsze przynajmniej jeden ułamek.
// Zad. 3,4,5 w podtemacie korzystają z tego zadania:
//  - variant: "SEQ"   → bez nawiasu/potęgi potęgi
//  - variant: "PAREN" → część czynników w nawiasie do potęgi
// W treści zawsze co najmniej DWIE RÓŻNE liczby z rodziny (np. 3 i 27).

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const range = (a, b) => Array.from({ length: b - a + 1 }, (_, i) => a + i);
const factorPowLatex = (num, exp) =>
  exp === 1 ? `${num}` : `${num}^{${exp}}`;

const EQUIV = {
  4:   { base: 2, power: 2 },
  8:   { base: 2, power: 3 },
  9:   { base: 3, power: 2 },
  27:  { base: 3, power: 3 },
  25:  { base: 5, power: 2 },
  125: { base: 5, power: 3 },
};

function normalizeToFamily(base, exp) {
  if (base === 2 || base === 3 || base === 5) return { base, exp };
  const eq = EQUIV[base];
  if (!eq) return null;
  return { base: eq.base, exp: exp * eq.power };
}

function parsePowerLike(raw) {
  if (!raw) return null;
  const s = raw.replace(/\s/g, "");

  let m = s.match(/^(\d+)\^\-?(\d+)$/);
  if (m) {
    const base = parseInt(m[1], 10);
    const exp = s.includes("^-")
      ? -parseInt(m[2], 10)
      : parseInt(m[2], 10);
    return { kind: "pow", base, exp };
  }

  m = s.match(/^1\/(\d+)\^(\+)?(\d+)$/); // 1/a^n
  if (m)
    return {
      kind: "fracPow",
      base: parseInt(m[1], 10),
      exp: -parseInt(m[3], 10),
    };

  m = s.match(/^1\/\((\d+)\^(\d+)\)$/); // 1/(a^n)
  if (m)
    return {
      kind: "fracPowParen",
      base: parseInt(m[1], 10),
      exp: -parseInt(m[2], 10),
    };

  return null;
}

// === ładne formatowanie wpisu użytkownika (jak w Zadaniu 2) ===
function prettyUserLatex(raw) {
  if (!raw) return "";
  const s = String(raw).replace(/\s/g, "");
  let m;

  // 1/(a^k)
  m = s.match(/^1\/\((\d+)\^(\d+)\)$/);
  if (m) return `\\dfrac{1}{${m[1]}^{${m[2]}}}`;

  // 1/a^k
  m = s.match(/^1\/(\d+)\^(\d+)$/);
  if (m) return `\\dfrac{1}{${m[1]}^{${m[2]}}}`;

  // a^-k  lub a^k
  m = s.match(/^(\d+)\^\-?(\d+)$/);
  if (m) {
    const neg = s.includes("^-");
    return `${m[1]}^{${neg ? "-" : ""}${m[2]}}`;
  }

  // fallback – pokaż tak jak wpisano
  return s;
}

/**
 * config:
 *  - variant?: "SEQ" | "PAREN"  // jeśli brak → losujemy
 */
export function generateTask(config = {}) {
  const forcedVariant = config.variant || null;

  const familyBase = pick([2, 3, 5]);
  const fam =
    familyBase === 2
      ? { a: 2, a2: 4, a3: 8, r2: 2, r3: 3 }
      : familyBase === 3
      ? { a: 3, a2: 9, a3: 27, r2: 2, r3: 3 }
      : { a: 5, a2: 25, a3: 125, r2: 2, r3: 3 };

  const candidates = [
    { num: fam.a, powMul: 1 },
    { num: fam.a2, powMul: fam.r2 },
    { num: fam.a3, powMul: fam.r3 },
  ];

  // === generator pojedynczego czynnika ===
  function makeTerm({ preferFraction = false } = {}) {
    const baseObj = pick(candidates);
    const k = pick(range(1, 9));
    const expSign = Math.random() < 0.3 ? -1 : 1;
    const expShown = expSign * k;

    const type = preferFraction
      ? "fraction"
      : pick(["normal", "divide", "fraction"]);

    let latex, contrib, explanation;

    if (type === "normal") {
      const prod = baseObj.powMul * expShown;
      latex = factorPowLatex(baseObj.num, expShown);
      contrib = prod;
      explanation = {
        original: latex,
        toBase: `(${familyBase}^{${baseObj.powMul}})^{${expShown}}`,
        simplified: `${familyBase}^{${prod}}`,
      };
    } else if (type === "divide") {
      // Dodatni wykładnik w zapisie, minus pojawia się dopiero w wykładniku
      // po przejściu do odwrotności.
      const kPos = Math.abs(k);
      const prod = baseObj.powMul * kPos;
      latex = factorPowLatex(baseObj.num, kPos);
      contrib = -prod;
      explanation = {
        original: latex,
        toBase: `(${familyBase}^{${baseObj.powMul}})^{${kPos}}`,
        simplified: `${familyBase}^{-${prod}}`,
        asDivision: true,
      };
    } else {
      // Ułamek 1/a^k — zawsze dodatni k, znak minus w wykładniku po przejściu
      const absK = Math.abs(k);
      const prod = baseObj.powMul * absK;
      latex = `\\dfrac{1}{${factorPowLatex(baseObj.num, absK)}}`;
      contrib = -prod;
      explanation = {
        original: latex,
        toBase: `\\dfrac{1}{(${familyBase}^{${baseObj.powMul}})^{${absK}}}`,
        simplified: `${familyBase}^{-${prod}}`,
        isFraction: true,
      };
    }

    // num – rzeczywista liczba z rodziny, np. 3 / 9 / 27.
    return { latex, contrib, explanation, type, num: baseObj.num };
  }

  // --- SEQ: dokładnie 3 czynniki, pierwszy zawsze ułamek ---
  function buildSEQ() {
    const terms = [];
    for (let i = 0; i < 3; i++) {
      terms.push(makeTerm({ preferFraction: i === 0 }));
    }

    const ops = [];
    for (let i = 0; i < 2; i++) {
      ops.push(terms[i + 1].type === "divide" ? ":" : "·");
    }

    const K = terms.reduce((s, t) => s + t.contrib, 0);

    let leftLatex = "";
    for (let i = 0; i < terms.length; i++) {
      leftLatex += terms[i].latex;
      if (i < ops.length)
        leftLatex += ops[i] === ":" ? "\\,:\\," : "\\,\\cdot\\,";
    }

    return {
      pattern: "SEQ",
      base: fam.a,
      leftLatex,
      K,
      seq: { terms, ops },
    };
  }

  // --- PAREN: część w nawiasie do potęgi, reszta na zewnątrz ---
  function buildPAREN() {
    const t = pick([2, 3, 4]);
    const innerCount = pick([1, 2]);
    const outerCount = 3 - innerCount;

    const inner = [];
    const outer = [];

    for (let i = 0; i < innerCount; i++) {
      inner.push(makeTerm({ preferFraction: i === 0 }));
    }
    for (let i = 0; i < outerCount; i++) {
      outer.push(makeTerm());
    }

    const innerOps = [];
    for (let i = 0; i < inner.length - 1; i++) {
      innerOps.push(inner[i + 1].type === "divide" ? ":" : "·");
    }

    let innerLatex = "";
    for (let i = 0; i < inner.length; i++) {
      innerLatex += inner[i].latex;
      if (i < innerOps.length)
        innerLatex += innerOps[i] === ":" ? "\\,:\\," : "\\,\\cdot\\,";
    }

    let leftLatex = `\\big(${innerLatex}\\big)^{${t}}`;

    const outerOps = [];
    for (let i = 0; i < outer.length; i++) {
      const op = outer[i].type === "divide" ? ":" : "·";
      outerOps.push(op);
      leftLatex += op === ":" ? "\\,:\\," : "\\,\\cdot\\,";
      leftLatex += outer[i].latex;
    }

    const innerSum = inner.reduce((s, x) => s + x.contrib, 0);
    const outerSum = outer.reduce((s, x) => s + x.contrib, 0);
    const K = innerSum * t + outerSum;

    return {
      pattern: "PAREN",
      base: fam.a,
      leftLatex,
      K,
      paren: {
        t,
        inner,
        innerOps,
        innerSum,
        outer,
        outerOps,
        outerSum,
      },
    };
  }

  // warunek: w całym wyrażeniu muszą wystąpić co najmniej DWIE różne liczby
  // z rodziny (np. 3 i 9, 3 i 27, 9 i 27 itd.)
  function isVariedEnough(built) {
    const nums = new Set();
    if (built.pattern === "SEQ") {
      built.seq.terms.forEach((t) => nums.add(t.num));
    } else {
      built.paren.inner.forEach((t) => nums.add(t.num));
      built.paren.outer.forEach((t) => nums.add(t.num));
    }
    return nums.size >= 2;
  }

  let built = null;
  const maxTries = 400;
  for (let tries = 0; tries < maxTries && !built; tries++) {
    const variant =
      forcedVariant === "SEQ" || forcedVariant === "PAREN"
        ? forcedVariant
        : pick(["SEQ", "PAREN"]);

    const candidate = variant === "SEQ" ? buildSEQ() : buildPAREN();
    if (!isVariedEnough(candidate)) {
      continue; // odrzucamy przypadki typu same 3-ki / same 27-ki
    }
    built = candidate;
  }

  if (!built) {
    built = buildSEQ(); // awaryjnie
  }

  return {
    type: "MixedFamilyIntegerToSinglePowerTask",
    data: {
      base: fam.a,       // 2, 3 lub 5 – PODSTAWA WYNIKU
      familyBase,        // to samo, ale jawnie
      leftLatex: built.leftLatex,
      K: built.K,
      __meta: built,
    },
  };
}

export function renderTask({ data }) {
  const { base, leftLatex } = data;
  return (
    <div style={{ marginBottom: "12px" }}>
      <div>
        Przedstaw w postaci jednej potęgi o podstawie{" "}
        <InlineMath math={`${base}`} />:
      </div>
      <BlockMath math={leftLatex} />
    </div>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  const handleChange = (ans) => onChange?.(ans);
  return (
    <div>
      <FractionInput
        value={userAnswer}
        onChange={handleChange}
        disabled={disabled}
        placeholder={"np. 5^-2 lub 5^2"}
        powerPreview={true}
        label=""
      />
      <div aria-hidden style={{ marginTop: "-16px" }} />
      <div aria-hidden style={{ height: "12px" }} />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw)
    return false;
  const parsed = parsePowerLike(userAnswer.raw);
  if (!parsed) return false;
  const norm = normalizeToFamily(parsed.base, parsed.exp);
  if (!norm) return false;
  return norm.base === data.base && norm.exp === data.K;
}

export function renderUserAnswer(userAnswer, task) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw)
    return <i>(brak odpowiedzi)</i>;
  const latex = prettyUserLatex(userAnswer.raw);
  return <InlineMath math={latex} />;
}

export function renderAnswer({ data }) {
  const { base, K } = data;
  return <InlineMath math={`${base}^{${K}}`} />;
}

/** Pomocnicza: zamienia pojedynczy składnik na wersję z ujemnym wykładnikiem,
 * bez zmiany podstawy (używane w Kroku 1). */
function toNegExponentLatex(termLatex, termType) {
  if (termType === "divide") {
    const m1 = termLatex.match(/^(\d+)$/);
    if (m1) return `${m1[1]}^{-1}`;
    const m2 = termLatex.match(/^(\d+)\^\{?(\d+)\}?$/);
    if (m2) return `${m2[1]}^{-${m2[2]}}`;
    return termLatex;
  }
  if (termType === "fraction") {
    const m = termLatex.match(
      /^\\dfrac\{1\}\{(\d+)\^\{?(\d+)\}?\}$/
    );
    if (m) return `${m[1]}^{-${m[2]}}`;
    return termLatex;
  }
  return termLatex; // "normal"
}

export function renderExplanation({ data }) {
  const { base, leftLatex, K, __meta } = data;

  const hint =
    base === 2
      ? "4=2^{2},\\;8=2^{3}"
      : base === 3
      ? "9=3^{2},\\;27=3^{3}"
      : "25=5^{2},\\;125=5^{3}";

  const ruleLine =
    "a^{m}\\cdot a^{n}=a^{m+n}\\quad a^{m}:a^{n}=a^{m-n}\\quad (a^{r})^{t}=a^{r\\cdot t}\\quad \\dfrac{1}{a^{n}}=a^{-n}";

  // --- KROK 1: zamiana dzielenia / ułamków na ujemne wykładniki ---
  let step1Needed = false;
  let step1Latex = "";

  if (__meta.pattern === "SEQ") {
    const pieces = [];
    for (let i = 0; i < __meta.seq.terms.length; i++) {
      const t = __meta.seq.terms[i];
      const transformed = toNegExponentLatex(t.latex, t.type);
      if (t.type === "divide" || t.type === "fraction") step1Needed = true;
      pieces.push(transformed);
    }
    step1Latex = pieces.join("\\,\\cdot\\,");
  } else {
    const innerPieces = __meta.paren.inner.map((t) => {
      const tr = toNegExponentLatex(t.latex, t.type);
      if (t.type === "divide" || t.type === "fraction") step1Needed = true;
      return tr;
    });
    const innerJoined = innerPieces.join("\\,\\cdot\\,");
    const outerPieces = __meta.paren.outer.map((t) => {
      const tr = toNegExponentLatex(t.latex, t.type);
      if (t.type === "divide" || t.type === "fraction") step1Needed = true;
      return tr;
    });
    step1Latex =
      `\\big(${innerJoined}\\big)^{${__meta.paren.t}}` +
      (outerPieces.length
        ? `\\,\\cdot\\,${outerPieces.join("\\,\\cdot\\,")}`
        : "");
  }

  const lines = [];

  if (__meta.pattern === "SEQ") {
    __meta.seq.terms.forEach((t) => {
      lines.push(
        `${t.explanation.original}\\;=\\;${t.explanation.toBase}\\;=\\;${t.explanation.simplified}`
      );
    });

    const contribs = __meta.seq.terms.map((t) => t.contrib);
    const line2 = contribs
      .map((c) => `${base}^{${c}}`)
      .join("\\,\\cdot\\,");
    const line3 = contribs
      .map((c, i) =>
        i === 0 ? `${c}` : c >= 0 ? `+${c}` : `${c}`
      )
      .join(" ");
    lines.push(line2);
    lines.push(`=\\;${base}^{${line3}}`);
    lines.push(`=\\;${base}^{${K}}`);
  } else {
    const innerOriginal = __meta.paren.inner
      .map((t, i) => {
        const piece = t.latex;
        if (i === __meta.paren.inner.length - 1) return piece;
        const op =
          __meta.paren.innerOps[i] === ":"
            ? "\\,:\\,"
            : "\\,\\cdot\\,";
        return piece + op;
      })
      .join("");

    __meta.paren.inner.forEach((t) => {
      lines.push(
        `${t.explanation.original}\\;=\\;${t.explanation.toBase}\\;=\\;${t.explanation.simplified}`
      );
    });

    const innerContribs = __meta.paren.inner.map((t) => t.contrib);
    const innerContribsProd = innerContribs
      .map((c) => `${base}^{${c}}`)
      .join("\\,\\cdot\\,");
    const innerSumExpr = innerContribs
      .map((c, i) =>
        i === 0 ? `${c}` : c >= 0 ? `+${c}` : `${c}`
      )
      .join(" ");
    lines.push(`\\big(${innerOriginal}\\big)^{${__meta.paren.t}}`);
    lines.push(
      `=\\;\\big(${innerContribsProd}\\big)^{${__meta.paren.t}}`
    );
    lines.push(
      `=\\;\\big(${base}^{${innerSumExpr}}\\big)^{${__meta.paren.t}}`
    );
    lines.push(
      `=\\;${base}^{(${innerSumExpr})\\cdot ${__meta.paren.t}}`
    );
    lines.push(`=\\;${base}^{${__meta.paren.innerSum * __meta.paren.t}}`);

    __meta.paren.outer.forEach((t) => {
      lines.push(
        `${t.explanation.original}\\;=\\;${t.explanation.toBase}\\;=\\;${t.explanation.simplified}`
      );
    });

    const outerContribs = __meta.paren.outer.map((t) => t.contrib);
    const factors = [
      `${base}^{${__meta.paren.innerSum * __meta.paren.t}}`,
      ...outerContribs.map((c) => `${base}^{${c}}`),
    ];
    const sumExpr = [
      __meta.paren.innerSum * __meta.paren.t,
      ...outerContribs,
    ]
      .map((c, i) =>
        i === 0 ? `${c}` : c >= 0 ? `+${c}` : `${c}`
      )
      .join(" ");
    lines.push(factors.join("\\,\\cdot\\,"));
    lines.push(`=\\;${base}^{${sumExpr}}`);
    lines.push(`=\\;${base}^{${K}}`);
  }

  return (
    <div style={{ display: "grid", rowGap: "8px" }}>
      {step1Needed && (
        <>
          <p>
            <strong>Krok 1:</strong> Zamień dzielenie na mnożenie przez
            odwrotność (dopisz minus do wykładnika).
          </p>
          <BlockMath math={`${leftLatex}\\;=\\;${step1Latex}`} />
        </>
      )}

      <p>
        <strong>Krok {step1Needed ? 2 : 1}:</strong> Zamień liczby z rodziny
        na potęgi wspólnej podstawy.
      </p>
      <BlockMath math={hint} />

      <p>
        <strong>Krok {step1Needed ? 3 : 2}:</strong> Skorzystaj z zasad
        działań na potęgach.
      </p>
      <BlockMath math={ruleLine} />

      <p>
        <strong>Krok {step1Needed ? 4 : 3}:</strong> Szczegółowy rachunek.
      </p>
      <BlockMath math={leftLatex} />
      {lines.map((ln, idx) => (
        <BlockMath key={idx} math={ln} />
      ))}
    </div>
  );
}
